/**
 * What the interaction prompt tells the game report (P1.9-T17), and the switch a measurement turns it off with: what
 * it names after each frame, how many labels it has set and how many times the target lookup has scanned the view,
 * which grow only when the target's verb or the things in reach change, and on request where its parts stand.
 */

/**
 * The prompt as the report reads it: the target's handle, verb and label, whether it is drawn, whether it is greyed
 * (the key withheld while the character is stunned) with the key cap's and the label's tints, on which side of
 * the bracket its row stands, the UI scale it was laid out at, where its parts stand in logical pixels (the bracket's
 * box, the key cap's top left and width, the legend's and the label's top lefts and the label's width), how many
 * display objects it is, and whether they sit in the UI layer, which the UI camera draws over the whole world.
 */
export interface PromptReading {
  readonly handle: number;
  readonly verb: string;
  readonly label: string;
  readonly shown: boolean;
  readonly greyed: boolean;
  readonly keyTint: number;
  readonly labelTint: number;
  readonly below: boolean;
  readonly scale: number;
  readonly bracketX: number;
  readonly bracketY: number;
  readonly bracketWidth: number;
  readonly bracketHeight: number;
  readonly keyX: number;
  readonly keyY: number;
  readonly keyWidth: number;
  /** Whether the key cap and its legend are drawn: false on a page with no keyboard in use. */
  readonly keyShown: boolean;
  /** The key the cap names: the label of the key Interact is bound to. */
  readonly legend: string;
  readonly legendX: number;
  readonly legendY: number;
  readonly labelX: number;
  readonly labelY: number;
  readonly labelWidth: number;
  /** The refusal under the row, empty for none, its text's top left and its tint (0 before one was shown). */
  readonly reason: string;
  readonly reasonX: number;
  readonly reasonY: number;
  readonly reasonTint: number;
  readonly objects: number;
  readonly inUiLayer: boolean;
}

/**
 * The prompt's report and switch. `enabled` false hides the prompt and stops it asking for the target, for a
 * measurement of the frame without it; `shown`, `handle`, `verb` and `label` are what it drew on the last frame
 * (-1 and empty strings when nothing), `greyed` whether it drew them with the key withheld, `reason` the refusal it
 * drew under them (empty for none); `labels` counts every
 * label it has set, `scans` the target lookup's scans of the view; `read` lays its parts out for a test, never on a
 * frame.
 */
export interface PromptReport {
  enabled: boolean;
  shown: boolean;
  handle: number;
  verb: string;
  label: string;
  greyed: boolean;
  reason: string;
  labels: number;
  scans: number;
  read: () => PromptReading | null;
  /** Whether a press, in device pixels, lands on the prompt (its row or its bracket) while it is shown. */
  hit: (x: number, y: number) => boolean;
}

/**
 * A report with nothing drawn yet.
 *
 * @returns The report, reading nothing until the prompt is created.
 */
export function createPromptReport(): PromptReport {
  return {
    enabled: true,
    shown: false,
    handle: -1,
    verb: "",
    label: "",
    greyed: false,
    reason: "",
    labels: 0,
    scans: 0,
    read: () => null,
    hit: () => false,
  };
}
